// ────────────────────────────────────────────────────────────────────────────
// The two controls that move an order's status, and the line that explains a
// requirement it does not meet.
//
// They live in DIFFERENT parts of the page — advancing sits in the header under
// the order number, beside the status it changes; cancelling stays in the
// footer where the delete control was, because abandoning an order is not the
// same gesture as progressing one and should not sit next to it. Both are
// driven by `useOrderActions`, which owns the shared confirmation.
// ────────────────────────────────────────────────────────────────────────────
import { cn } from '@my/ui';
import { AlertTriangle, ArrowRight, Ban, Loader2 } from 'lucide-react';
import { requirementMeta } from '@/features/settings/workflows/lib/mockWorkflow';
import type { Unmet } from '@/features/settings/workflows/lib/evaluate';
import type { MoveAssessment } from '../lib/orderWorkflow';

export interface MoveControlProps {
  move: MoveAssessment;
  busy?: boolean;
  /** Why the control is unavailable for a reason outside the workflow. */
  disabledReason?: string;
  onClick: () => void;
}

/** One unmet requirement, named the way the settings screen names it. */
export function UnmetLine({ unmet }: { unmet: Unmet }) {
  const meta = requirementMeta(unmet.kind);
  return (
    <li className="flex items-start gap-2">
      {unmet.severity === 'stop' ? (
        <Ban className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
      ) : (
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
      )}
      <span className="text-xs text-slate-700">
        <span className="font-medium">{meta.label}</span>
        {' — '}
        {unmet.reason}
      </span>
    </li>
  );
}

/** The blocked reasons, one per line, for a tooltip. */
function stopReasons(move: MoveAssessment): string {
  return move.unmet
    .filter((u) => u.severity === 'stop')
    .map((u) => `${requirementMeta(u.kind).label} — ${u.reason}`)
    .join('\n');
}

/**
 * Advancing, as an icon.
 *
 * <p>
 * Icon-only because the DESTINATION changes with the state: "Move to Open"
 * fits, "Move to In Progress" does not, and a button whose width jumps as an
 * order progresses makes its row restless. The destination lives in the
 * tooltip and the accessible name instead, and the status sits right beside it.
 * </p>
 */
export function OrderAdvanceButton({ move, busy, disabledReason, onClick }: MoveControlProps) {
  const warns = move.unmet.filter((u) => u.severity === 'warn');

  // The tooltip always names the destination first — with an icon-only control
  // it is the only place an operator can read what the button will do.
  const title = [
    `Move to ${move.toLabel}`,
    move.blocked
      ? stopReasons(move)
      : (disabledReason ??
        (warns.length > 0
          ? `${warns.length} warning${warns.length === 1 ? '' : 's'} — you will be asked to confirm`
          : '')),
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <button
      type="button"
      aria-label={`Move to ${move.toLabel}`}
      onClick={onClick}
      disabled={move.blocked || !!busy || !!disabledReason}
      title={title}
      className={cn(
        // 28px rather than the footer's 32: it sits under an eyebrow label
        // beside the service-tag chips, and the larger control read as heavy
        // against them.
        'relative inline-flex h-7 w-7 items-center justify-center rounded-md',
        'bg-sky-600 text-white hover:bg-sky-700',
        'disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <ArrowRight className="h-3.5 w-3.5" />
      )}
      {/* A warning badge on the corner: the move is allowed, but it will ask
          first, and that is worth knowing before clicking rather than after. */}
      {!move.blocked && warns.length > 0 && (
        <AlertTriangle className="absolute -right-1 -top-1 h-3 w-3 text-amber-500" />
      )}
    </button>
  );
}

/**
 * Cancelling, as the icon control that used to delete.
 *
 * <p>
 * There is no hard delete: an order carries an allocated number, so abandoning
 * it should leave a Cancelled record rather than a hole in the sequence. A
 * blocked cancellation still shows the button, disabled, with the reason in its
 * title — an operator who cannot abandon an invoiced order needs to be told
 * why, and an absent control tells them nothing.
 * </p>
 */
export function OrderCancelButton({ move, busy, disabledReason, onClick }: MoveControlProps) {
  const title = move.blocked
    ? `Cannot cancel — ${stopReasons(move)}`
    : (disabledReason ?? 'Cancel this order');

  return (
    <button
      type="button"
      aria-label="Cancel order"
      onClick={onClick}
      disabled={move.blocked || !!busy || !!disabledReason}
      title={title}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-sm text-red-600',
        'hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40',
      )}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}
    </button>
  );
}
